'use client';

import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Modal } from '@/components/_shared/Modal/Modal';
import { WT_SURVEY_DISMISS_ACTIONS, WT_SURVEY_ILLUSTRATION_SRC, WT_SURVEY_TYPEFORM_URL } from './constants';
import type { UseWtSurveyEligibilityResult } from './useWtSurveyEligibility';
import styles from './WtSurvey.module.scss';

type WtSurveyModalProps = {
  isOpen: boolean;
  onDismiss: UseWtSurveyEligibilityResult['dismiss'];
  onClickCta: UseWtSurveyEligibilityResult['click'];
};

const HEADLINE_ID = 'wt-survey-headline';

export const WtSurveyModal = ({ isOpen, onDismiss, onClickCta }: WtSurveyModalProps) => {
  const { formatMessage } = useIntl();
  // The static artwork is served beneath the app's base path.
  const { basePath } = useRouter();
  const handleCtaClick = useCallback(() => {
    onClickCta();
  }, [onClickCta]);

  // Capture phase so this runs before the listing's own key handlers.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss(WT_SURVEY_DISMISS_ACTIONS.escapeKey);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, onDismiss]);

  // A single `click` listener, not mousedown/touchstart: dismissing on mousedown
  // unmounts the modal before the matching click lands, so it falls through to
  // the listing underneath.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (!target.closest('[data-spec="eds-modal-body"]')) {
        onDismiss(WT_SURVEY_DISMISS_ACTIONS.overlay);
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isOpen, onDismiss]);

  return (
    // The close controls are rendered here rather than by EDS so each dismissal
    // can report its own reason.
    <Modal
      visible={isOpen}
      type="simple"
      isClosable={false}
      ariaLabelledby={HEADLINE_ID}
      __containerClassName={styles.modal}
    >
      <div className={styles.content} data-testid="wt-survey-modal">
        <div className={styles.illustration}>
          <Image
            src={`${basePath}${WT_SURVEY_ILLUSTRATION_SRC}`}
            alt=""
            width={1024}
            height={1024}
            priority
            className={styles.illustrationImage}
            data-testid="wt-survey-illustration"
          />
        </div>

        <h2 id={HEADLINE_ID} className={styles.headline}>
          {formatMessage({
            id: 'How do you feel about file sharing?',
            defaultMessage: 'How do you feel about file sharing?',
            description: 'WeTransfer file-sharing survey modal headline',
          })}
        </h2>

        <p className={styles.subtitle}>
          {formatMessage({
            id: 'Share your impressions in our short survey. It takes less than 2 minutes!',
            defaultMessage: 'Share your impressions in our short survey. It takes less than 2 minutes!',
            description: 'WeTransfer file-sharing survey modal supporting copy',
          })}
        </p>

        <a
          className={styles.cta}
          href={WT_SURVEY_TYPEFORM_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleCtaClick}
        >
          {formatMessage({
            id: 'Take the survey',
            defaultMessage: 'Take the survey',
            description: 'WeTransfer file-sharing survey modal CTA',
          })}
        </a>

        <button
          type="button"
          className={styles.close}
          onClick={() => onDismiss(WT_SURVEY_DISMISS_ACTIONS.closeButton)}
          aria-label={formatMessage({
            id: 'Close survey',
            defaultMessage: 'Close survey',
            description: 'Accessible label for the WeTransfer survey modal close button',
          })}
          data-testid="wt-survey-close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </Modal>
  );
};
